import React, { useState } from 'react';
import { X, RefreshCcw, Loader2 } from 'lucide-react';
import type { Stage1SummaryField } from '@/api/client';

/** Canonical row order matches the Summary tab so the diff reads top
 *  to bottom in the same sequence the drafter sees in the cards. */
const FIELDS: Array<{ apiField: Stage1SummaryField; label: string }> = [
  { apiField: 'overall',        label: 'Overall Invention' },
  { apiField: 'problem',        label: 'Problem' },
  { apiField: 'solution',       label: 'Solution' },
  { apiField: 'implementation', label: 'Implementation' },
  { apiField: 'prior_art',      label: 'Prior Art' },
];

/** API field shape used inside the modal. Strings for the four text
 *  fields, string[] for prior art. Both `currentFields` and `snapshot`
 *  use the same keys (`overall` / `problem` / ...) the PATCH endpoint
 *  already accepts, so revert is just a PATCH with the snapshot value. */
export interface DiffFieldMap {
  overall?: string;
  problem?: string;
  solution?: string;
  implementation?: string;
  prior_art?: string[];
}

interface DiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Drafter's current values, derived from
   *  ``stage_state.content.summary``. Missing keys are treated as
   *  empty / [] so the equality check still produces a sensible row. */
  currentFields: DiffFieldMap;
  /** AI-extracted baseline pulled from ``idf_ai_snapshot``. Null while
   *  the snapshot is being fetched — the modal still opens but warns
   *  the drafter that no comparison can be shown yet. */
  snapshot: DiffFieldMap | null;
  isFrozen: boolean;
  /** Reverts a single field to its snapshot value via PATCH and then
   *  refreshes the matter. Implemented in the parent so the modal
   *  doesn't need its own API client knowledge of the field-name
   *  mapping. */
  onRevert: (
    field: Stage1SummaryField,
    snapshotValue: string | string[],
  ) => Promise<void>;
}

function eq(
  a: string | string[] | undefined,
  b: string | string[] | undefined,
): boolean {
  // Treat undefined as the field's empty representation so a missing
  // snapshot key matches an empty current value (and vice-versa).
  const aNorm = a ?? (Array.isArray(b) ? [] : '');
  const bNorm = b ?? (Array.isArray(a) ? [] : '');
  if (Array.isArray(aNorm) && Array.isArray(bNorm)) {
    if (aNorm.length !== bNorm.length) return false;
    return aNorm.every((v, i) => v === bNorm[i]);
  }
  return aNorm === bNorm;
}

function renderValue(v: string | string[] | undefined): React.ReactNode {
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-zinc-400 italic">none</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {v.map((s, i) => (
          <span
            key={i}
            className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-700"
          >
            {s}
          </span>
        ))}
      </div>
    );
  }
  if (!v) return <span className="text-zinc-400 italic">empty</span>;
  return (
    <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{v}</p>
  );
}

export default function DiffModal({
  isOpen, onClose, currentFields, snapshot, isFrozen, onRevert,
}: DiffModalProps) {
  // Per-field reverting flag so the right button shows a spinner
  // without freezing the rest of the modal.
  const [reverting, setReverting] = useState<Stage1SummaryField | null>(null);

  if (!isOpen) return null;

  const haveSnapshot = !!snapshot;
  const changed = FIELDS.filter(
    ({ apiField }) => haveSnapshot && !eq(currentFields[apiField], snapshot![apiField]),
  );
  const unchanged = FIELDS.filter(
    ({ apiField }) => haveSnapshot && eq(currentFields[apiField], snapshot![apiField]),
  );

  const handleRevert = async (field: Stage1SummaryField) => {
    if (!snapshot || isFrozen) return;
    setReverting(field);
    try {
      const snapVal = snapshot[field];
      // Default empty value matches the API contract: empty string for
      // text fields, empty array for prior art.
      const value = field === 'prior_art'
        ? (Array.isArray(snapVal) ? snapVal : [])
        : (typeof snapVal === 'string' ? snapVal : '');
      await onRevert(field, value);
    } finally {
      setReverting(null);
    }
  };

  return (
    <div
      data-testid="diff-vs-ai-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-white rounded-sm border border-zinc-200 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-zinc-200">
          <div className="min-w-0">
            <h3
              className="text-base font-semibold text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Diff vs AI
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Compare your edits against the original AI extraction. Revert
              any field individually.
            </p>
          </div>
          <button
            type="button"
            data-testid="diff-vs-ai-close"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 transition-colors flex-shrink-0"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!haveSnapshot && (
            <div
              data-testid="diff-vs-ai-no-snapshot"
              className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900"
            >
              No AI snapshot has been recorded yet. It is built the first
              time the Summary tab opens — close this modal and re-open
              after a moment.
            </div>
          )}

          {/* Changed fields — full two-column comparison + revert */}
          {changed.map(({ apiField, label }) => (
            <div
              key={apiField}
              data-testid={`diff-row-changed-${apiField}`}
              className="border border-amber-200 bg-amber-50/40 rounded-sm p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-zinc-900">{label}</h4>
                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                  Edited
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                    AI Original
                  </div>
                  <div
                    data-testid={`diff-ai-${apiField}`}
                    className="bg-white border border-zinc-200 rounded-sm p-2.5 min-h-[3rem]"
                  >
                    {renderValue(snapshot![apiField])}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7] mb-1">
                    Your Version
                  </div>
                  <div
                    data-testid={`diff-current-${apiField}`}
                    className="bg-white border border-blue-200 rounded-sm p-2.5 min-h-[3rem]"
                  >
                    {renderValue(currentFields[apiField])}
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid={`diff-revert-${apiField}`}
                  onClick={() => handleRevert(apiField)}
                  disabled={isFrozen || reverting === apiField}
                  className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {reverting === apiField
                    ? <Loader2 size={12} className="animate-spin" />
                    : <RefreshCcw size={12} />}
                  Revert to AI
                </button>
              </div>
            </div>
          ))}

          {/* Unchanged fields — single collapsed row each */}
          {unchanged.length > 0 && (
            <div data-testid="diff-unchanged-list" className="space-y-1 pt-2 border-t border-zinc-100">
              {unchanged.map(({ apiField, label }) => (
                <div
                  key={apiField}
                  data-testid={`diff-row-unchanged-${apiField}`}
                  className="text-xs text-zinc-500 px-1 py-0.5 flex items-center gap-2"
                >
                  <span className="text-zinc-400">·</span>
                  <span>{label} — unchanged</span>
                </div>
              ))}
            </div>
          )}

          {haveSnapshot && changed.length === 0 && (
            <div
              data-testid="diff-vs-ai-no-changes"
              className="text-sm text-zinc-500 italic text-center py-4"
            >
              All fields match the AI extraction.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end">
          <button
            type="button"
            data-testid="diff-vs-ai-done"
            onClick={onClose}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-xs font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
